import { LitElement, html, css } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';
import '../../elements/langhout-button/langhout-button';
import {
  formatDateInput,
  formatDateTime,
  formatHourMinute,
  formatJSDate,
  hasScope,
  formatTime,
} from '../../util/util';
import {
  arrowLeftIcon,
  arrowRightIcon,
  closeIcon,
} from '../../elements/icons/icons';
import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import { SearchDropdown } from '../../elements/search-dropdown/search-dropdown';
import { showNotification } from '../../LanghoutBetonPortal';
import { Product, Comment } from '../../types/types';
import { JWT_KEY_LOCALSTORAGE } from '../../environment/environment';
import { sharedStyles } from '../../elements/shared-styles';

enum PlannedProductionType {
  Production = 'production',
  Maintenance = 'maintenance',
  Malfunction = 'malfunction',
  Conversion = 'conversion',
  Special = 'special',
}

type Day = {
  date: Date;
  hour_list: Hour[];
};

type ProductionUnit = {
  id: string;
  name: string;
};

type Hour = {
  hour: number;
  hour_formatted: string;
  minutes: number;
  machines: Map<string, DrawableEvent[]>;
};

type PlannedProduction = {
  id: string;
  start: Date;
  end: Date;
  product_id: string;
  product_description: string;
  amount: number;
  preview: boolean;
  production_unit_id: string;
  ready_date: Date;
  explanation: string;
  planned_production_type: PlannedProductionType;
  order_id: number;
  shipping_id: string;
  customer_name: string;
  shipping_city: string;
  email_notification_address: string;
  comments: Comment[];
};

type CreatePlannedProductionRequest = {
  start: string;
  end: string;
  production_unit_id: string;
  ready_date: string;
  amount: number;
  product_id: string;
  explanation: string;
  planned_production_type: PlannedProductionType;
  order_id: number;
  shipping_id: string;
  customer_name: string;
  shipping_city: string;
  email_notification_address: string;
};

type DragPosition = {
  hour: number;
  minutes: number;
};

type DrawableEvent = {
  id: string;
  start: Date;
  end: Date;
  ready_date: Date;
  isStart: boolean;
  isEnd: boolean;
  height: number;
  top: number;
  product: string;
  product_id: string;
  production_unit_id: string;
  amount: number;
  preview: boolean;
  planned_production_type: PlannedProductionType;
  explanation: string;
  order_id: number;
  shipping_id: string;
  customer_name: string;
  shipping_city: string;
  email_notification_address: string;
  comments: Comment[];
};

type DayPlanningResponse = {
  M172: PlanningResponse[];
  M184: PlanningResponse[];
  M204: PlanningResponse[];
};

type PlanningResponse = {
  id: string;
  start: string;
  end: string;
  production_unit_id: string;
  ready_date: string;
  amount: number;
  product_id: string;
  product_description: string;
  planned_production_type: PlannedProductionType;
  explanation: string;
  order_id: number;
  shipping_id: string;
  customer_name: string;
  shipping_city: string;
  email_notification_address: string;
  comments: Comment[];
};

@customElement('langhout-beton-view-production-planning')
export class ViewProductionPlanning extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        font-size: 17px;
        color: var(--text-color-main);
        height: calc(100vh - 84px);
        max-height: calc(100vh - 84px);
        overflow-y: auto;
        background-color: white;
      }

      :host([disableButtons]) {
        pointer-events: none;
        opacity: 0.7;
      }

      .wrapper {
        margin-top: 50px;
        height: calc(100vh - 204px);
        display: flex;
        flex-direction: column;
      }

      .column {
        width: 100%;
        min-width: 180px;
        padding: 0;
      }

      .row {
        height: 50px;
        min-height: 50px;
        max-height: 50px;
        width: 100%;
        display: flex;
        flex-direction: row;
      }

      .centered-column {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .first-row,
      .second-row {
        height: 50px;
        min-height: 50px;
        max-height: 50px;
      }

      .cal-header {
        height: 50px;
        font-weight: bold;
        font-size: 20px;
        color: var(--primary-color);
        text-align: center;
      }

      .cal-header-today {
        background-color: rgba(1, 78, 42, 0.4);
        border-radius: 10px;
      }

      .item-wrapper {
        height: 100%;
        width: 33%;
        min-width: 57px;
        position: relative;
      }

      .calendar-item-start {
        margin-top: 1px;
        border-top-left-radius: 5px;
        border-top-right-radius: 5px;
      }

      .calendar-item-end {
        margin-bottom: 1px;
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;
      }

      .calendar-item {
        left: 0.5px;
        padding-top: 5px;
        padding-bottom: 5px;
        padding-left: 4px;
        padding-right: 2px;
        position: absolute;
        width: 90%;
        z-index: 20;
        text-align: left;
        font-size: 16px;
        line-height: 20px;
        color: white;
        background-color: var(--primary-color);
        word-wrap: break-word;
        word-break: break-word;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        flex-grow: 0;
      }

      .calendar-item-production,
      .modal-type-production {
        background-color: var(--primary-color);
      }

      .calendar-item-maintenance,
      .modal-type-maintenance {
        background-color: var(--text-color-main);
      }

      .calendar-item-conversion,
      .modal-type-conversion {
        background-color: orange;
      }

      .calendar-item-malfunction,
      .modal-type-malfunction {
        background-color: red;
      }

      .calendar-item-special,
      .modal-type-special {
        background-color: #01a5e8;
      }

      .product-text {
        margin-top: 10px;
      }

      .calendar-item-text {
        color: white;
        padding-left: 5px;
        text-align: left;
        margin-top: -5px;
        line-height: 6px;
        width: 95%;
        z-index: 21;
        font-size: 14px;
      }

      .product-span {
        margin-top: 10px;
        width: 70%;
        line-height: 14px;
        word-wrap: break-word;
        padding-right: 2px;
      }

      .machine-item-wrapper {
        height: 100%;
        width: 33%;
        min-width: 57px;
        font-size: 16px;
        font-weight: bold;
      }

      .item-wrapper-border-left {
        border-left: 1px solid black;
      }

      .item-wrapper-border-right {
        border-right: 1px solid black;
      }

      .item-wrapper-border-top {
        border-top: 1px solid black;
      }

      .item-wrapper-border-right-green {
        border-right: 2px solid var(--primary-color);
      }

      .item-wrapper-border-left-green {
        border-left: 1px solid var(--primary-color);
      }

      .item-wrapper:hover {
        cursor: pointer;
      }

      .time-row-top {
        height: 50px;
        min-height: 50px;
      }

      .time-column {
        width: 50px;
        display: flex;
        flex-direction: column;
      }

      .time-column-item {
        height: 100%;
        font-size: 11px;
        font-weight: bold;
        width: 50px;
        line-height: 0;
      }

      .time-line-extension {
        margin-top: 1px;
        height: 49px;
        width: 10px;
        border-bottom: 1px solid black;
      }

      .navigation {
        margin-bottom: 20px;
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .display-month-year {
        text-transform: capitalize;
        margin-left: 50px;
      }

      .prev,
      .next {
        margin-left: 10px;
        margin-top: 5px;
      }

      svg {
        width: 50px;
        height: 50px;
      }

      .navigation-button-wrapper {
        display: flex;
        flex-direction: row;
        margin-left: 14px;
      }

      .navigation-button-wrapper:hover {
        cursor: pointer;
      }

      .week {
        margin-left: 15px;
      }

      .month-year {
        font-weight: bold;
        font-size: 25px;
        margin-left: 15px;
      }

      .today-button {
        margin-left: 50px;
      }

      #modal {
        position: absolute;
        transform: translateY(-3000px);
        transition-duration: 1s;

        left: 0;
        right: 0;
        margin-left: auto;
        margin-right: auto;
        width: 100%;
        max-width: 600px;
        display: flex;
        flex-direction: column;
        align-items: center;
        z-index: 50;
        max-height: calc(100% - 220px);
        overflow: hidden;
        padding-bottom: 2px;
        padding-top: 2px;
        border-radius: 10px;
      }

      :host([modalVisible]) #modal {
        transition-duration: 500ms;
        transform: translateY(60px);
      }

      .modal-content {
        color: var(--text-color-light);
        border-radius: 10px;
        box-shadow: -8px 8px 6px -6px black;
        width: calc(100% - 50px);
        padding: 20px;
        padding-bottom: 250px;
        overflow-y: auto;
      }

      input {
        max-width: calc(100% - 220px);
      }

      .label {
        text-align: left;
        width: 200px;
      }

      .data-entry-row {
        margin-top: 15px;
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      search-dropdown {
        max-width: calc(100% - 200px);
      }

      .button-row {
        margin-top: 20px;
      }

      .amount-row {
        margin-top: 20px;
      }

      langhout-button {
        margin-top: 10px;
        margin-left: 10px;
        min-width: 120px;
      }

      select {
        padding: 5px;
        height: 37.19px;
        border-radius: 5px;
        border: 2px solid black;
        font-size: 16px;
      }

      textarea {
        padding: 10px;
        width: 100%;
        max-width: calc(100% - 222px);
        height: 100px;
      }

      .comment-wrapper {
        width: 100%;
        padding: 10px;
        border: 2px solid white;
        margin-top: 15px;
        border-radius: 5px;
      }

      .comment-user-name,
      .comment-time {
        font-weight: bold;
        color: white;
      }
    `,
  ];

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  @property({ type: Boolean, reflect: true })
  public modalVisible?: boolean = false;

  @property({ type: Array, reflect: true })
  private currentDateRange: Day[] = [];
  private currentDate: Date = new Date();
  private calendarMonday: Date = new Date();
  private hourList: Hour[] = [];

  private isMouseDown = false;
  private editID = '';
  private edit = false;
  private dragMachine = '';
  private dragDayX = 0;
  private startHour = 0;
  private dragStart = 0;
  @property({ type: Object, reflect: true })
  private startDrag?: DragPosition;
  @property({ type: Object, reflect: true })
  private endDrag?: DragPosition;

  @property({ type: String, reflect: true })
  private startTimeString = '';

  @property({ type: String, reflect: true })
  private endTimeString = '';

  @property({ type: String, reflect: true })
  private readyDateString = '';

  @property({ type: String, reflect: true })
  private explanation = '';

  @property({ type: Array, reflect: true })
  private comments: Comment[] = [];

  private productionPlanningDay: Map<string, PlannedProduction[]>[] = [];
  private months: string[] = [
    'januari',
    'februari',
    'maart',
    'april',
    'mei',
    'juni',
    'juli',
    'augustus',
    'september',
    'oktober',
    'november',
    'december',
  ];

  private weekDays: string[] = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
  private productionUnitNameToIDMap: Map<string, string> = new Map<
    string,
    string
  >();
  private productionUnitIDToNameMap: Map<string, string> = new Map<
    string,
    string
  >();

  @query('#calendarView')
  public viewSelect?: HTMLSelectElement;

  @query('#eventStartTime')
  public eventStartTime?: HTMLInputElement;

  @query('#eventEndTime')
  public eventEndTime?: HTMLInputElement;

  @query('#readyDate')
  public readyDate?: HTMLInputElement;

  @query('#selectedProduct')
  public selectedProduct?: SearchDropdown;

  @query('#amount')
  public amount?: HTMLInputElement;

  @query('#planningType')
  public planningTypeElement?: HTMLSelectElement;

  @property({ type: String, reflect: true })
  public selectedPlanningType: PlannedProductionType =
    PlannedProductionType.Production;

  @property({ type: String, reflect: true })
  public selectedProductID = '';

  @property({ type: String, reflect: true })
  public selectedProductDescription = '';

  @property({ type: String, reflect: true })
  public selectedAmount = '0';

  @query('#explanation')
  public explanationElement?: HTMLTextAreaElement;

  @query('#orderId')
  public orderIdElement?: HTMLInputElement;

  @query('#shippingId')
  public shippingIdElement?: HTMLInputElement;

  @query('#customerName')
  public customerNameElement?: HTMLInputElement;

  @query('#shippingCity')
  public shippingCityElement?: HTMLInputElement;

  @query('#emailNotificationAddress')
  public emailNotificationAddressElement?: HTMLInputElement;

  @property({ type: String, reflect: true })
  public selectedOrderId = '';

  @property({ type: String, reflect: true })
  public selectedShippingId = '';

  @property({ type: String, reflect: true })
  public selectedCustomerName = '';

  @property({ type: String, reflect: true })
  public selectedShippingCity = '';

  @property({ type: String, reflect: true })
  public selectedEmailNotificationAddress = 'expeditie@langhout-beton.nl';

  @query('#comment')
  public commentElement?: HTMLTextAreaElement;

  // amount of days shown in the calendar
  private amountOfDaysViewed = 5;
  // number when next or previous is pressed.
  private amountOfDaysNext = 7;

  private hourHeight = 50;

  private canEdit = true;

  constructor() {
    super();
    this.setInitDate();
    this.getProductionUnitsCall();
    this.getPlannedProductionCall();

    const jwt = localStorage.getItem(JWT_KEY_LOCALSTORAGE);
    if (!jwt) {
      return;
    }
    const isProductionPlanningEditor = hasScope(
      jwt,
      'production-planning-editor',
    );
    this.canEdit = isProductionPlanningEditor;
  }

  render() {
    return html`
      ${this.renderModal()}
      <div class="navigation">
        <langhout-button
          class="today-button"
          text="Vandaag"
          @click="${() => {
            this.setInitDate();
            this.getPlannedProductionCall();
          }}"
        ></langhout-button>
        <div class="fill"></div>
        <div class="centered-column">
          <select
            id="calendarView"
            name="calanderView"
            @change="${() => {
              if (!this.viewSelect) {
                return;
              }
              const val = this.viewSelect.value;
              if (val === '7') {
                this.amountOfDaysViewed = 7;
                this.amountOfDaysNext = 7;
              } else if (val === '5') {
                this.amountOfDaysViewed = 5;
                this.amountOfDaysNext = 7;
              } else if (val === '1') {
                this.amountOfDaysViewed = 1;
                this.amountOfDaysNext = 1;
              }
              this.setInitDate();
              this.getPlannedProductionCall();
            }}"
          >
            <option value="1">1 dag</option>
            <option .selected=${true} value="5">5 dagen</option>
            <option value="7">7 dagen</option>
          </select>
        </div>
        <div class="centered-column">
          <div class="navigation-button-wrapper">
            <div
              class="prev"
              @click="${() => {
                this.calendarMonday.setDate(
                  this.calendarMonday.getDate() - this.amountOfDaysNext,
                );
                this.getPlannedProductionCall();
              }}"
            >
              ${arrowLeftIcon()}
            </div>
            <div
              class="next"
              @click="${() => {
                this.calendarMonday.setDate(
                  this.calendarMonday.getDate() + this.amountOfDaysNext,
                );
                this.getPlannedProductionCall();
              }}"
            >
              ${arrowRightIcon()}
            </div>
          </div>
        </div>

        <div class="centered-column month-year">
          ${this.months[this.calendarMonday.getMonth()]}
        </div>
        <div class="centered-column month-year">
          ${this.calendarMonday.getFullYear()}
        </div>
        <div class="centered-column week">
          Week ${this.getWeek(this.calendarMonday)}
        </div>
      </div>

      <div class="wrapper">
        <div class="row">
          <div class="time-column">
            <div class="row time-row-top"></div>
            <div class="row time-row-top"></div>
            <div class="row time-row-top"></div>
            ${this.hourList.map((hour: Hour) => {
              return html`
                <div class="row">
                  <div class="time-column-item">
                    ${this.getHourFormattedValue(hour)}
                  </div>
                  <div class="${this.getTimeColumnHeader(hour)}"></div>
                </div>
              `;
            })}
          </div>

          ${this.currentDateRange.map((day: Day, x: number) => {
            const calHeaderClasses = ['cal-header'];
            if (this.currentDate.getDate() === day.date.getDate()) {
              calHeaderClasses.push('cal-header-today');
            }
            return html`
              <div class="column">
                <div class="column first-row">
                  <div>${this.weekDays[day.date.getDay()]}</div>
                </div>
                <div class="column second-row ${calHeaderClasses.join(' ')}">
                  <div>${day.date.getDate()}</div>
                </div>
                <div class="row">
                  <div class="machine-item-wrapper">M172</div>
                  <div class="machine-item-wrapper">M184</div>
                  <div class="machine-item-wrapper">M204</div>
                </div>
                ${day.hour_list.map((hour: Hour, y: number) => {
                  return html`
                    <div class="row">
                      <div
                        @mousedown="${(e: MouseEvent) => {
                          if (
                            this.shouldIgnoreDrag(hour.machines.get('M172'))
                          ) {
                            return;
                          }
                          this.handleDragMouseDown('M172', hour, x, y, e);
                        }}"
                        @mouseup="${() => {
                          this.handleDragMouseUp();
                        }}"
                        @mousemove="${(e: MouseEvent) => {
                          this.handleDragMouseMove(hour, e.clientY);
                        }}"
                        class="item-wrapper y-${y}x-${x} ${this.getItemWrapperClassLeft(
                          x,
                          this.currentDateRange.length,
                          y,
                        )}"
                      >
                        ${this.renderDrawableEvents(hour.machines.get('M172'))}
                      </div>
                      <div
                        @mousedown="${(e: MouseEvent) => {
                          if (
                            this.shouldIgnoreDrag(hour.machines.get('M184'))
                          ) {
                            return;
                          }
                          this.handleDragMouseDown('M184', hour, x, y, e);
                        }}"
                        @mouseup="${() => {
                          this.handleDragMouseUp();
                        }}"
                        @mousemove="${(e: MouseEvent) => {
                          this.handleDragMouseMove(hour, e.clientY);
                        }}"
                        class="item-wrapper y-${y}x-${x} ${this.getItemWrapperClassMiddle(
                          x,
                          this.currentDateRange.length,
                          y,
                        )}"
                      >
                        ${this.renderDrawableEvents(hour.machines.get('M184'))}
                      </div>
                      <div
                        @mousedown="${(e: MouseEvent) => {
                          if (
                            this.shouldIgnoreDrag(hour.machines.get('M204'))
                          ) {
                            return;
                          }
                          this.handleDragMouseDown('M204', hour, x, y, e);
                        }}"
                        @mouseup="${() => {
                          this.handleDragMouseUp();
                        }}"
                        @mousemove="${(e: MouseEvent) => {
                          this.handleDragMouseMove(hour, e.clientY);
                        }}"
                        class="item-wrapper y-${y}x-${x} ${this.getItemWrapperClassRight(
                          x,
                          this.currentDateRange.length,
                          y,
                        )}"
                      >
                        ${this.renderDrawableEvents(hour.machines.get('M204'))}
                      </div>
                    </div>
                  `;
                })}
              </div>
            `;
          })}
        </div>
      </div>
    `;
  }

  shouldIgnoreDrag = (drawableEvents: DrawableEvent[] | undefined) => {
    if (!drawableEvents || drawableEvents.length === 0) {
      return false;
    }

    let height = 0;
    drawableEvents.forEach((d: DrawableEvent) => {
      height += d.height;
    });
    return height >= 100;
  };

  planEvent = async () => {
    const plannedProductionPayload = this.createPlannedProductionPayload();
    if (!plannedProductionPayload) {
      return;
    }
    const success = await this.createPlannedProductionCall(
      plannedProductionPayload,
    );
    if (!success) {
      return;
    }

    this.resetModal();
    showNotification('Productie planning aangemaakt');
  };

  editEvent = async () => {
    const plannedProductionPayload = this.createPlannedProductionPayload();
    if (!plannedProductionPayload) {
      return;
    }
    const success = await this.updatePlannedProductionCall(
      plannedProductionPayload,
    );
    if (!success) {
      return;
    }

    this.resetModal();
    showNotification('Productie planning bijgewerkt');
  };

  deleteEvent = async () => {
    const success = await this.deletePlannedProductionCall();
    if (!success) {
      return;
    }

    this.resetModal();
    showNotification('Productie planning verwijderd');
  };

  renderModal = () => {
    return html`
      <div id="modal">
        <div class="modal-content modal-type-${this.selectedPlanningType}">
          <div class="row">
            <div class="fill"></div>
            <div
              class="close-icon"
              @click="${() => {
                this.resetModal();
              }}"
            >
              ${closeIcon()}
            </div>
          </div>
          <div class="data-entry-row">
            <div class="label">Start</div>
            <input
              type="datetime-local"
              id="eventStartTime"
              name="start-time"
              .value="${this.startTimeString}"
            />
          </div>
          <div class="data-entry-row">
            <div class="label">End</div>
            <input
              type="datetime-local"
              id="eventEndTime"
              name="end-time"
              .value="${this.endTimeString}"
            />
          </div>
          <div class="data-entry-row">
            <div class="label">Machine</div>
            <div>${this.dragMachine}</div>
          </div>
          <div class="data-entry-row">
            <div class="label">Soort</div>
            <div>
              <select
                name="planning-type"
                id="planningType"
                @change="${() => {
                  if (!this.planningTypeElement) {
                    return;
                  }
                  this.selectedPlanningType = this.planningTypeElement
                    .value as PlannedProductionType;
                }}"
              >
                <option
                  .selected=${this.selectedPlanningType === PlannedProductionType.Production}
                  value="production"
                >
                  Productie
                </option>
                <option
                  .selected=${this.selectedPlanningType === PlannedProductionType.Maintenance}
                  value="maintenance"
                >
                  Onderhoud
                </option>
                <option
                  .selected=${this.selectedPlanningType === PlannedProductionType.Conversion}
                  value="conversion"
                >
                  Ombouw
                </option>
                <option
                  .selected=${this.selectedPlanningType === PlannedProductionType.Malfunction}
                  value="malfunction"
                >
                  Storing
                </option>
                <option
                  .selected=${this.selectedPlanningType === PlannedProductionType.Special}
                  value="special"
                >
                  Speciaal
                </option>
              </select>
            </div>
          </div>
          ${this.renderModalContent()}
          <div class="data-entry-row button-row">
            ${this.renderModalControls()}
          </div>
          ${this.renderCommentSection()}
        </div>
      </div>
    `;
  };

  renderCommentSection = () => {
    return html`
      <div class="data-entry-row">
        <div class="label">Opmerking</div>
        <textarea type="text" id="comment" name="comment"></textarea>
      </div>
      <div class="data-entry-row">
        <div class="fill"></div>
        <langhout-button
          reverse
          text="Opmerking plaatsen"
          @click="${async () => {
            this.disableButtons = true;
            if (!this.commentElement) {
              this.disableButtons = false;
              return;
            }
            if (this.commentElement.value === '') {
              this.disableButtons = false;
              showNotification('Opmerking moet worden ingevuld');
              return;
            }
            await this.addCommentCall(this.commentElement.value);
            this.resetModal();
            this.disableButtons = false;
          }}"
        ></langhout-button>
      </div>
      ${this.renderComments(this.comments)}
    `;
  };

  renderComments = (comments: Comment[]) => {
    if (!comments || comments.length === 0) {
      return html``;
    }
    return html`
      <div class="data-entry-row">
        <h1>Opmerkingen</h1>
      </div>
      ${comments.map((comment: Comment) => {
        return html`
          <div class="data-entry-row">
            <div class=" comment-wrapper">
              <div class="row">
                <div class="comment-user-name">${comment.user_name}</div>
                <div class="fill"></div>
                <div class="comment-time">
                  ${formatTime(comment.timestamp, false)}
                </div>
              </div>
              <div class="row comment-message">${comment.message}</div>
            </div>
          </div>
        `;
      })}
    `;
  };

  renderModalContent = () => {
    if (
      this.selectedPlanningType === PlannedProductionType.Malfunction ||
      this.selectedPlanningType === PlannedProductionType.Maintenance
    ) {
      return html`
        <div class="data-entry-row">
          <div class="label">Toelichting</div>
          <textarea
            .value="${this.explanation}"
            type="text"
            id="explanation"
            name="explanation"
          ></textarea>
        </div>
      `;
    }
    if (this.selectedPlanningType === PlannedProductionType.Conversion) {
      return html`
        <div class="data-entry-row">
          <div class="label">Toelichting</div>
          <textarea
            .value="${this.explanation}"
            type="text"
            id="explanation"
            name="explanation"
          ></textarea>
        </div>
        <div class="data-entry-row">
          <div class="label">Product</div>
          <search-dropdown
            id="selectedProduct"
            .selected="${<Product>{
              id: this.selectedProductID,
              description: this.selectedProductDescription,
            }}"
          ></search-dropdown>
        </div>
      `;
    }
    if (this.selectedPlanningType === PlannedProductionType.Special) {
      return html`
        <div class="data-entry-row">
          <div class="label">Datum gereed</div>
          <input
            type="date"
            id="readyDate"
            name="readyDate"
            .value="${this.readyDateString}"
          />
        </div>
        <div class="data-entry-row">
          <div class="label">Product</div>
          <search-dropdown
            id="selectedProduct"
            .selected="${<Product>{
              id: this.selectedProductID,
              description: this.selectedProductDescription,
            }}"
          ></search-dropdown>
        </div>
        <div class="data-entry-row amount-row">
          <div class="label">Aantal</div>
          <input
            type="number"
            id="amount"
            name="amount"
            .value="${live(this.selectedAmount)}"
          />
        </div>
        <div class="data-entry-row">
          <div class="label">Ordernummer LH</div>
          <input
            type="number"
            id="orderId"
            name="orderId"
            .value="${this.selectedOrderId}"
            @input="${() => {
              if (!this.orderIdElement) return;
              const val = this.orderIdElement.value;
              if (val.length >= 6) {
                this.searchOrderHeaderExact(val);
              }
            }}"
          />
        </div>
        <div class="data-entry-row">
          <div class="label">Ordernummer klant</div>
          <input
            type="text"
            id="shippingId"
            name="shippingId"
            .value="${live(this.selectedShippingId)}"
          />
        </div>
        <div class="data-entry-row">
          <div class="label">Klantnaam</div>
          <input
            type="text"
            id="customerName"
            name="customerName"
            .value="${live(this.selectedCustomerName)}"
          />
        </div>
        <div class="data-entry-row">
          <div class="label">Plaatsnaam</div>
          <input
            type="text"
            id="shippingCity"
            name="shippingCity"
            .value="${live(this.selectedShippingCity)}"
          />
        </div>
        <div class="data-entry-row">
          <div class="label">E-mail gereedmelding</div>
          <input
            type="email"
            id="emailNotificationAddress"
            name="emailNotificationAddress"
            .value="${this.selectedEmailNotificationAddress}"
          />
        </div>
      `;
    }

    return html`
      <div class="data-entry-row">
        <div class="label">Datum gereed</div>
        <input
          type="date"
          id="readyDate"
          name="readyDate"
          .value="${this.readyDateString}"
        />
      </div>
      <div class="data-entry-row">
        <div class="label">Product</div>
        <search-dropdown
          id="selectedProduct"
          .selected="${<Product>{
            id: this.selectedProductID,
            description: this.selectedProductDescription,
          }}"
        ></search-dropdown>
      </div>
      <div class="data-entry-row amount-row">
        <div class="label">Aantal</div>
        <input
          type="number"
          id="amount"
          name="amount"
          .value="${live(this.selectedAmount)}"
        />
      </div>
    `;
  };

  renderModalControls = () => {
    if (!this.canEdit) {
      return html``;
    }
    if (this.edit) {
      return html`
        <langhout-button
          text="Verwijderen"
          removeButton
          @click="${async () => {
            this.disableButtons = true;
            await this.deleteEvent();
            this.disableButtons = false;
          }}"
        ></langhout-button>
        <div class="fill"></div>
        <langhout-button
          reverse
          text="Voorraad bekijken"
          @click="${() => {
            if (this.selectedProduct && this.selectedProduct.selected) {
              window.location.href = `/view-stock?id=${this.selectedProduct.selected.id}`;
            }
          }}"
        ></langhout-button>
        <langhout-button
          reverse
          text="Aanpassen"
          @click="${async () => {
            this.disableButtons = true;
            await this.editEvent();
            this.disableButtons = false;
          }}"
        ></langhout-button>
      `;
    }
    return html`
      <div class="fill"></div>
      <langhout-button
        reverse
        text="Inplannen"
        @click="${async () => {
          this.disableButtons = true;
          await this.planEvent();
          this.disableButtons = false;
        }}"
      ></langhout-button>
    `;
  };

  createPlannedProductionPayload = ():
    CreatePlannedProductionRequest | undefined => {
    // Sync DOM values to properties so they survive re-renders
    if (this.selectedProduct && this.selectedProduct.selected) {
      this.selectedProductID = this.selectedProduct.selected.id;
      this.selectedProductDescription =
        this.selectedProduct.selected.description;
    }
    if (this.amount) {
      this.selectedAmount = this.amount.value;
    }

    if (!this.eventStartTime || this.eventStartTime.value === '') {
      showNotification('Event start moet worden ingevuld');
      return undefined;
    }
    if (!this.eventEndTime || this.eventEndTime.value === '') {
      showNotification('Event eind moet worden ingevuld');
      return undefined;
    }
    const startTime = new Date(this.eventStartTime.value);
    const endTime = new Date(this.eventEndTime.value);
    if (endTime < startTime) {
      showNotification('Eindtijd moet na starttijd zijn');
      return undefined;
    }
    if (startTime.getDate() !== endTime.getDate()) {
      showNotification('Start en eind datum moeten gelijk zijn');
      return undefined;
    }
    const production_unit_id = this.productionUnitNameToIDMap.get(
      this.dragMachine,
    );
    if (!production_unit_id) {
      showNotification('Machine niet gevonden');
      return undefined;
    }
    const plannedProductionPayload = <CreatePlannedProductionRequest>{
      start: startTime.toISOString(),
      end: endTime.toISOString(),
      production_unit_id: production_unit_id,
      planned_production_type: this.selectedPlanningType,
    };

    if (this.selectedPlanningType === PlannedProductionType.Production) {
      if (!this.readyDate || this.readyDate.value === '') {
        showNotification('Datum gereed moet worden ingevuld');
        return undefined;
      }
      plannedProductionPayload.ready_date = new Date(
        this.readyDate.value,
      ).toISOString();

      if (
        !this.amount ||
        this.amount.value === '' ||
        isNaN(parseInt(this.amount.value))
      ) {
        showNotification(
          'Aantal (stuks) moet worden ingevuld als geheel getal',
        );
        return undefined;
      }
      plannedProductionPayload.amount = parseInt(this.amount.value);
    }
    if (
      this.selectedPlanningType === PlannedProductionType.Conversion ||
      this.selectedPlanningType === PlannedProductionType.Production
    ) {
      if (!this.selectedProduct || !this.selectedProduct.selected) {
        showNotification('Product moet worden geselecteerd');
        return undefined;
      }
      plannedProductionPayload.product_id = this.selectedProduct.selected.id;
    }

    if (
      (this.selectedPlanningType === PlannedProductionType.Malfunction ||
        this.selectedPlanningType === PlannedProductionType.Conversion ||
        this.selectedPlanningType === PlannedProductionType.Maintenance) &&
      this.explanationElement
    ) {
      plannedProductionPayload.explanation = this.explanationElement.value;
    }

    if (this.selectedPlanningType === PlannedProductionType.Special) {
      if (!this.readyDate || this.readyDate.value === '') {
        showNotification('Datum gereed moet worden ingevuld');
        return undefined;
      }
      plannedProductionPayload.ready_date = new Date(
        this.readyDate.value,
      ).toISOString();

      console.log(plannedProductionPayload.ready_date);

      if (!this.selectedProduct || !this.selectedProduct.selected) {
        showNotification('Product moet worden geselecteerd');
        return undefined;
      }
      plannedProductionPayload.product_id = this.selectedProduct.selected.id;

      if (
        !this.amount ||
        this.amount.value === '' ||
        isNaN(parseInt(this.amount.value))
      ) {
        showNotification(
          'Aantal (stuks) moet worden ingevuld als geheel getal',
        );
        return undefined;
      }
      plannedProductionPayload.amount = parseInt(this.amount.value);

      if (this.orderIdElement) {
        plannedProductionPayload.order_id =
          parseInt(this.orderIdElement.value) || 0;
      }
      if (this.shippingIdElement) {
        plannedProductionPayload.shipping_id = this.shippingIdElement.value;
      }
      if (this.customerNameElement) {
        plannedProductionPayload.customer_name = this.customerNameElement.value;
      }
      if (this.shippingCityElement) {
        plannedProductionPayload.shipping_city = this.shippingCityElement.value;
      }
      if (this.emailNotificationAddressElement) {
        plannedProductionPayload.email_notification_address =
          this.emailNotificationAddressElement.value;
      }
    }

    return plannedProductionPayload;
  };

  defaultReadyDate = () => {
    if (!this.startDrag) {
      return '';
    }
    const calendarMondayCopy = new Date(this.calendarMonday);
    calendarMondayCopy.setDate(
      calendarMondayCopy.getDate() - ((calendarMondayCopy.getDay() + 6) % 7),
    );
    calendarMondayCopy.setDate(
      calendarMondayCopy.getDate() + this.dragDayX + 3,
    );
    calendarMondayCopy.setHours(this.startDrag.hour);
    calendarMondayCopy.setMinutes(this.startDrag.minutes);
    return calendarMondayCopy.toISOString();
  };

  setInitDate = () => {
    if (this.amountOfDaysViewed === 1) {
      this.calendarMonday = new Date();
      return;
    }
    const calendarMonday = new Date();
    calendarMonday.setDate(
      calendarMonday.getDate() - ((calendarMonday.getDay() + 6) % 7),
    );
    this.calendarMonday = calendarMonday;
  };

  emptyProductionPlanningDay = (): Map<string, PlannedProduction[]> => {
    const emptyDayPlanning = new Map<string, PlannedProduction[]>();
    emptyDayPlanning.set('M172', []);
    emptyDayPlanning.set('M184', []);
    emptyDayPlanning.set('M204', []);
    return emptyDayPlanning;
  };

  copyProductionPlanning = (
    productionPlanning: Map<string, PlannedProduction[]>[],
  ): Map<string, PlannedProduction[]>[] => {
    const newArray = <Map<string, PlannedProduction[]>[]>[];
    productionPlanning.forEach(p => {
      const emptyPlanning = this.emptyProductionPlanningDay();
      emptyPlanning.set(
        'M172',
        Array.from(this.getPlannedProduction('M172', p)),
      );
      emptyPlanning.set(
        'M184',
        Array.from(this.getPlannedProduction('M184', p)),
      );
      emptyPlanning.set(
        'M204',
        Array.from(this.getPlannedProduction('M204', p)),
      );
      newArray.push(emptyPlanning);
    });

    return newArray;
  };

  getPlannedProduction = (
    key: string,
    map: Map<string, PlannedProduction[]>,
  ): PlannedProduction[] => {
    const test = map.get(key);
    if (!test) {
      return [];
    }
    return test;
  };

  buildCurrentDateRange = (
    productionPlanningWeek: Map<string, PlannedProduction[]>[],
  ) => {
    const productionPlanningCopy = this.copyProductionPlanning(
      productionPlanningWeek,
    );
    if (this.startDrag && this.endDrag) {
      const x = new Date(this.calendarMonday);
      x.setDate(x.getDate() + this.dragDayX);

      x.setMinutes(this.startDrag.minutes);
      x.setHours(this.startDrag.hour);
      x.setSeconds(0);

      const y = new Date(this.calendarMonday);
      y.setDate(y.getDate() + this.dragDayX);

      y.setMinutes(this.endDrag.minutes);
      y.setHours(this.endDrag.hour);
      y.setSeconds(0);
      let start = x;
      let end = y;
      if (y < x) {
        start = y;
        end = x;
      }
      if (end.getHours() === 0) {
        end.setHours(23);
      }
      const tempProdPlanningForDay = productionPlanningCopy[this.dragDayX].get(
        this.dragMachine,
      );
      if (!tempProdPlanningForDay) {
        return;
      }
      tempProdPlanningForDay.push(<PlannedProduction>{
        id: 'drag',
        start: start,
        end: end,
        preview: true,
      });
    }

    const hourList = this.getHourList(this.emptyProductionPlanningDay());
    this.hourList = hourList;

    const dayList: Day[] = [];
    for (let i = 0; i < productionPlanningCopy.length; i++) {
      const x = new Date(this.calendarMonday);
      x.setDate(x.getDate() + i);
      const hourList = this.getHourList(productionPlanningCopy[i]);

      dayList.push(<Day>{
        date: x,
        hour_list: hourList,
      });
    }
    this.currentDateRange = dayList;
  };

  getHourList = (
    productionPlanningDay: Map<string, PlannedProduction[]>,
  ): Hour[] => {
    const hourList: Hour[] = [];
    for (let i = 0; i < 24; i++) {
      let hourFormatted = `0${i}:00`;
      if (i > 9) {
        hourFormatted = `${i}:00`;
      }
      const machineMap = new Map<string, DrawableEvent[]>();
      machineMap.set('M172', []);
      machineMap.set('M184', []);
      machineMap.set('M204', []);
      hourList.push(<Hour>{
        hour_formatted: hourFormatted,
        hour: i,
        minutes: i * 60,
        machines: machineMap,
      });
    }

    return this.setProductionPlanning(hourList, productionPlanningDay);
  };

  setProductionPlanning = (
    hourList: Hour[],
    productionPlanningDay: Map<string, PlannedProduction[]>,
  ): Hour[] => {
    const machines = ['M172', 'M184', 'M204'];
    machines.forEach((m: string) => {
      const list = this.getPlannedProduction(m, productionPlanningDay);
      for (let i = 0; i < list.length; i++) {
        hourList.forEach((h: Hour) => {
          const drawableEvent = this.getDrawableEventForHour(h, list[i]);
          if (drawableEvent) {
            h.machines.get(m)?.push(drawableEvent);
          }
        });
      }
    });
    return hourList;
  };

  getDrawableEventForHour = (
    h: Hour,
    plannedProduction: PlannedProduction,
  ): DrawableEvent | undefined => {
    if (
      h.hour >= plannedProduction.start.getHours() &&
      h.hour <= plannedProduction.end.getHours()
    ) {
      const isStart = plannedProduction.start.getHours() == h.hour;
      let top = 0;
      if (isStart) {
        const minutes = plannedProduction.start.getMinutes();
        const height = 100 * ((60 - minutes) / 60);
        top = this.hourHeight - (this.hourHeight * height) / 100;
      }
      if (!isStart) {
        return undefined;
      }

      const totalMinutes =
        plannedProduction.end.getHours() * 60 +
        plannedProduction.end.getMinutes() -
        (plannedProduction.start.getHours() * 60 +
          plannedProduction.start.getMinutes());
      const heightReal = (totalMinutes / 60) * this.hourHeight - 13;
      return <DrawableEvent>{
        id: plannedProduction.id,
        isStart: true,
        isEnd: true,
        start: plannedProduction.start,
        end: plannedProduction.end,
        ready_date: plannedProduction.ready_date,
        height: heightReal,
        top: top,
        product: plannedProduction.product_description,
        product_id: plannedProduction.product_id,
        production_unit_id: plannedProduction.production_unit_id,
        amount: plannedProduction.amount,
        preview: plannedProduction.preview,
        planned_production_type: plannedProduction.planned_production_type,
        explanation: plannedProduction.explanation,
        order_id: plannedProduction.order_id,
        shipping_id: plannedProduction.shipping_id,
        customer_name: plannedProduction.customer_name,
        shipping_city: plannedProduction.shipping_city,
        email_notification_address:
          plannedProduction.email_notification_address,
        comments: plannedProduction.comments,
      };
    }
    return undefined;
  };

  getDragStartTimeString = (): string => {
    const dragStart = this.getPositionDateTime(this.startDrag);
    const dragEnd = this.getPositionDateTime(this.endDrag);
    if (dragEnd < dragStart) {
      return formatDateTime(dragEnd);
    }
    return formatDateTime(dragStart);
  };

  getDragEndTimeString = (): string => {
    const dragStart = this.getPositionDateTime(this.startDrag);
    const dragEnd = this.getPositionDateTime(this.endDrag);
    if (dragStart > dragEnd) {
      return formatDateTime(dragStart);
    }
    return formatDateTime(dragEnd);
  };

  getPositionDateTime = (dragPosition: DragPosition | undefined): Date => {
    if (!dragPosition) {
      return new Date();
    }
    const calendarMondayCopy = new Date(this.calendarMonday);
    if (this.amountOfDaysViewed !== 1) {
      calendarMondayCopy.setDate(
        calendarMondayCopy.getDate() - ((calendarMondayCopy.getDay() + 6) % 7),
      );
      calendarMondayCopy.setDate(calendarMondayCopy.getDate() + this.dragDayX);
    }
    calendarMondayCopy.setHours(dragPosition.hour);
    calendarMondayCopy.setMinutes(dragPosition.minutes);
    return calendarMondayCopy;
  };

  getWeek = (now: Date): number => {
    const d = new Date(
      Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()),
    );
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  };

  toDrawableEvents = (x: DrawableEvent[] | undefined): DrawableEvent[] => {
    if (!x) {
      return [];
    }
    return x;
  };

  handleDragMouseDown = (
    machine: string,
    hour: Hour,
    x: number,
    y: number,
    e: MouseEvent,
  ) => {
    if (this.modalVisible || !this.canEdit) {
      // this.resetModal();
      return;
    }
    this.isMouseDown = true;
    this.dragMachine = machine;
    this.dragDayX = x;
    this.startHour = hour.hour;
    this.dragStart = e.clientY;
    if (!this.shadowRoot) {
      return;
    }
    const test: HTMLDivElement | null = this.shadowRoot.querySelector(
      `.y-${y}x-${x}`,
    );
    if (!test) {
      return;
    }
    this.dragStart = test.getBoundingClientRect().top;

    this.startDrag = <DragPosition>{
      hour: hour.hour,
      minutes: 0,
    };
    this.endDrag = <DragPosition>{
      hour: hour.hour + 1,
      minutes: 0,
    };
    this.buildCurrentDateRange(this.productionPlanningDay);
  };

  resetModal = () => {
    this.modalVisible = false;
    this.dragMachine = '';
    this.startDrag = undefined;
    this.endDrag = undefined;
    this.edit = false;
    this.editID = '';
    this.selectedProductID = '';
    this.selectedProductDescription = '';
    this.selectedAmount = '0';
    if (this.selectedProduct) {
      this.selectedProduct.selected = undefined;
    }
    if (this.amount) {
      this.amount.value = '';
    }
    this.selectedOrderId = '';
    this.selectedShippingId = '';
    this.selectedCustomerName = '';
    this.selectedShippingCity = '';
    this.selectedEmailNotificationAddress = 'expeditie@langhout-beton.nl';

    this.getPlannedProductionCall();
  };

  handleDragMouseUp = () => {
    if (!this.isMouseDown || this.edit) {
      return;
    }

    this.startTimeString = this.getDragStartTimeString();
    this.endTimeString = this.getDragEndTimeString();
    this.selectedPlanningType = PlannedProductionType.Production;
    this.explanation = '';
    this.readyDateString = formatDateInput(this.defaultReadyDate());
    this.isMouseDown = false;
    this.modalVisible = true;
  };

  handleDragMouseMove = (hour: Hour, clientY: number) => {
    if (this.isMouseDown) {
      if (!this.startDrag || !this.shadowRoot) {
        return;
      }

      const difference = clientY - this.dragStart;

      let newHour = Math.floor(difference / this.hourHeight) + this.startHour;
      let minutesFactor = Math.floor(
        ((difference % this.hourHeight) / this.hourHeight) * 60,
      );
      if (difference > 0) {
        if (minutesFactor >= 0 && minutesFactor <= 15) {
          minutesFactor = 15;
        } else if (minutesFactor > 15 && minutesFactor <= 30) {
          minutesFactor = 30;
        } else if (minutesFactor > 30 && minutesFactor <= 45) {
          minutesFactor = 45;
        } else {
          minutesFactor = 0;
          newHour += 1;
        }
      }
      if (difference < 0) {
        if (minutesFactor < 0 && minutesFactor >= -15) {
          minutesFactor = 45;
        } else if (minutesFactor < -15 && minutesFactor >= -30) {
          minutesFactor = 30;
        } else if (minutesFactor < -30 && minutesFactor >= -45) {
          minutesFactor = 15;
        } else {
          minutesFactor = 0;
        }
      }

      if (difference <= 30 && difference > 0) {
        newHour = this.startDrag.hour + 1;
        minutesFactor = 0;
      }
      if (difference >= -30 && difference <= 0) {
        newHour = this.startDrag.hour - 1;
        minutesFactor = 0;
      }

      this.endDrag = <DragPosition>{
        hour: newHour,
        minutes: minutesFactor,
      };
      this.buildCurrentDateRange(this.productionPlanningDay);
    }
  };

  renderDrawableEvents = (drawableEvents: DrawableEvent[] | undefined) => {
    if (!drawableEvents || drawableEvents.length === 0) {
      return html``;
    }
    return html`
      ${drawableEvents.map((drawableEvent: DrawableEvent) => {
        return html`
            <div
              class="calendar-item calendar-item-${
                drawableEvent.planned_production_type
              } calendar-item-start calendar-item-end"
              style="top:${drawableEvent.top}px;height:${
                drawableEvent.height
              }px"
              @mousedown="${() => {
                this.clickEditPlannedProduction(drawableEvent);
              }}"
            >
           <span>
               ${formatHourMinute(drawableEvent.start.toISOString())} -
                ${formatHourMinute(drawableEvent.end.toISOString())}
            </span>
            ${this.renderDrawableEventText(drawableEvent)}
            </div>
          </div>
            
          `;
      })}
    `;
  };

  renderDrawableEventText = (drawableEvent: DrawableEvent) => {
    if (
      drawableEvent.planned_production_type ===
      PlannedProductionType.Malfunction
    ) {
      if (drawableEvent.explanation) {
        return html`
          <div class="product-text">${drawableEvent.explanation}</div>
        `;
      }
      return html` <div class="product-text">Storing</div> `;
    }
    if (
      drawableEvent.planned_production_type ===
      PlannedProductionType.Maintenance
    ) {
      if (drawableEvent.explanation) {
        return html`
          <div class="product-text">${drawableEvent.explanation}</div>
        `;
      }
      return html` <div class="product-text">Onderhoud</div> `;
    }
    if (
      drawableEvent.planned_production_type ===
        PlannedProductionType.Production ||
      drawableEvent.planned_production_type === PlannedProductionType.Conversion
    ) {
      return html` <div class="product-text">${drawableEvent.product}</div> `;
    }
    if (
      drawableEvent.planned_production_type === PlannedProductionType.Special
    ) {
      return html`
        <div class="product-text">${drawableEvent.product || 'Speciaal'}</div>
      `;
    }
  };

  clickEditPlannedProduction = (drawableEvent: DrawableEvent) => {
    this.startTimeString = formatDateTime(drawableEvent.start);
    this.endTimeString = formatDateTime(drawableEvent.end);
    this.selectedPlanningType = drawableEvent.planned_production_type;
    this.comments = drawableEvent.comments;
    this.explanation = '';
    this.explanation = drawableEvent.explanation;
    if (drawableEvent.ready_date.getFullYear() === 1) {
      // set to 3 days from now
      const today = new Date();
      today.setDate(today.getDate() + 1);
      this.readyDateString = formatDateInput(today.toDateString());
    } else {
      this.readyDateString = formatDateInput(
        drawableEvent.ready_date.toDateString(),
      );
    }
    this.selectedProductID = drawableEvent.product_id;
    this.selectedProductDescription = drawableEvent.product;
    this.selectedAmount = drawableEvent.amount.toString();
    this.selectedOrderId = drawableEvent.order_id
      ? drawableEvent.order_id.toString()
      : '';
    this.selectedShippingId = drawableEvent.shipping_id || '';
    this.selectedCustomerName = drawableEvent.customer_name || '';
    this.selectedShippingCity = drawableEvent.shipping_city || '';
    this.selectedEmailNotificationAddress =
      drawableEvent.email_notification_address || '';
    const machine = this.productionUnitIDToNameMap.get(
      drawableEvent.production_unit_id,
    );
    if (machine) {
      this.dragMachine = machine;
    }
    this.editID = drawableEvent.id;
    this.edit = true;
    this.modalVisible = true;
  };

  getItemWrapperClassLeft = (x: number, xTotal: number, y: number): string => {
    const classes = [];
    if (x != 0) {
      classes.push('item-wrapper-border-left-green');
    } else {
      classes.push('item-wrapper-border-left');
    }
    if (y != 0) {
      classes.push('item-wrapper-border-top');
    }

    return classes.join(' ');
  };

  getItemWrapperClassMiddle = (
    x: number,
    xTotal: number,
    y: number,
  ): string => {
    const classes = ['item-wrapper-border-left'];
    if (y != 0) {
      classes.push('item-wrapper-border-top');
    }

    return classes.join(' ');
  };

  getItemWrapperClassRight = (x: number, xTotal: number, y: number): string => {
    const classes = ['item-wrapper-border-left'];
    if (y != 0) {
      classes.push('item-wrapper-border-top');
    }
    if (x != xTotal - 1) {
      classes.push('item-wrapper-border-right-green');
    }

    return classes.join(' ');
  };

  getHourFormattedValue(hour: Hour): string {
    if (hour.hour === 0) {
      return '';
    }
    return hour.hour_formatted;
  }

  getTimeColumnHeader(hour: Hour): string {
    if (hour.hour === 23) {
      return '';
    }
    return 'time-line-extension';
  }

  addCommentCall = async (message: string): Promise<boolean> => {
    const commentPayload = {
      message: message,
      type: 'portal',
    };
    const result = await authenticatedAPICall(
      `/production-planning/${this.editID}/comment`,
      'POST',
      new Headers(),
      JSON.stringify(commentPayload),
    );
    if (isSuccessfulAPIResponse(result)) {
      return true;
    }
    return false;
  };

  createPlannedProductionCall = async (
    plannedProduction: CreatePlannedProductionRequest,
  ): Promise<boolean> => {
    const result = await authenticatedAPICall(
      `/production-planning`,
      'POST',
      new Headers(),
      JSON.stringify(plannedProduction),
    );
    if (isSuccessfulAPIResponse(result)) {
      return true;
    }
    return false;
  };

  updatePlannedProductionCall = async (
    plannedProduction: CreatePlannedProductionRequest,
  ): Promise<boolean> => {
    const result = await authenticatedAPICall(
      `/production-planning/${this.editID}`,
      'PUT',
      new Headers(),
      JSON.stringify(plannedProduction),
    );
    if (isSuccessfulAPIResponse(result)) {
      if (this.selectedProduct) {
        this.selectedProduct.selected = undefined;
      }
      if (this.amount) {
        this.amount.value = '';
      }
      return true;
    }
    return false;
  };

  deletePlannedProductionCall = async (): Promise<boolean> => {
    const result = await authenticatedAPICall(
      `/production-planning/${this.editID}`,
      'DELETE',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result)) {
      return true;
    }
    return false;
  };

  getPlannedProductionCall = async () => {
    const calendarMondayCopy = new Date(this.calendarMonday);
    const start = formatJSDate(this.calendarMonday);
    calendarMondayCopy.setDate(calendarMondayCopy.getDate() + 7);
    const end = formatJSDate(calendarMondayCopy);
    const result = await authenticatedAPICall(
      `/production-planning?start=${start}&end=${end}`,
      'GET',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result)) {
      this.productionPlanningDay = [];
      for (let i = 0; i < this.amountOfDaysViewed; i++) {
        this.productionPlanningDay.push(this.emptyProductionPlanningDay());
      }
      result.body.forEach((weekPlanning: DayPlanningResponse, i: number) => {
        if (i > this.productionPlanningDay.length - 1) {
          return;
        }
        if (weekPlanning.M172) {
          const plannedProduction: PlannedProduction[] = [];
          weekPlanning.M172.forEach(
            (plannedProductionResponse: PlanningResponse) => {
              plannedProduction.push(
                this.planningResponseToPlannedProduction(
                  plannedProductionResponse,
                ),
              );
            },
          );
          this.productionPlanningDay[i].set('M172', plannedProduction);
        }
        if (weekPlanning.M184) {
          const plannedProduction: PlannedProduction[] = [];
          weekPlanning.M184.forEach(
            (plannedProductionResponse: PlanningResponse) => {
              plannedProduction.push(
                this.planningResponseToPlannedProduction(
                  plannedProductionResponse,
                ),
              );
            },
          );
          this.productionPlanningDay[i].set('M184', plannedProduction);
        }
        if (weekPlanning.M204) {
          const plannedProduction: PlannedProduction[] = [];
          weekPlanning.M204.forEach(
            (plannedProductionResponse: PlanningResponse) => {
              plannedProduction.push(
                this.planningResponseToPlannedProduction(
                  plannedProductionResponse,
                ),
              );
            },
          );
          this.productionPlanningDay[i].set('M204', plannedProduction);
        }
      });
      this.buildCurrentDateRange(this.productionPlanningDay);
    }
  };

  planningResponseToPlannedProduction = (
    plannedProductionResponse: PlanningResponse,
  ): PlannedProduction => {
    return <PlannedProduction>{
      id: plannedProductionResponse.id,
      start: new Date(plannedProductionResponse.start),
      end: new Date(plannedProductionResponse.end),
      product_id: plannedProductionResponse.product_id,
      product_description: plannedProductionResponse.product_description,
      amount: plannedProductionResponse.amount,
      production_unit_id: plannedProductionResponse.production_unit_id,
      ready_date: new Date(plannedProductionResponse.ready_date),
      planned_production_type:
        plannedProductionResponse.planned_production_type,
      explanation: plannedProductionResponse.explanation,
      order_id: plannedProductionResponse.order_id,
      shipping_id: plannedProductionResponse.shipping_id,
      customer_name: plannedProductionResponse.customer_name,
      shipping_city: plannedProductionResponse.shipping_city,
      email_notification_address:
        plannedProductionResponse.email_notification_address,
      comments: plannedProductionResponse.comments,
    };
  };

  getProductionUnitsCall = async () => {
    const result = await authenticatedAPICall(
      `/production-unit`,
      'GET',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result)) {
      result.body.forEach((productionUnit: ProductionUnit) => {
        this.productionUnitNameToIDMap.set(
          productionUnit.name,
          productionUnit.id,
        );
        this.productionUnitIDToNameMap.set(
          productionUnit.id,
          productionUnit.name,
        );
      });
    }
  };

  searchOrderHeaderExact = async (orderNumber: string) => {
    const path = `/exact-globe/sales-order-header/${orderNumber}`;
    const result = await authenticatedAPICall(path, 'GET', new Headers());

    if (isSuccessfulAPIResponse(result)) {
      // Preserve current product and amount before re-render
      if (this.selectedProduct && this.selectedProduct.selected) {
        this.selectedProductID = this.selectedProduct.selected.id;
        this.selectedProductDescription =
          this.selectedProduct.selected.description;
      }
      if (this.amount) {
        this.selectedAmount = this.amount.value;
      }

      this.selectedShippingId = result.body.your_reference || '';
      this.selectedCustomerName = result.body.order_debtor_name || '';
      if (result.body.description2 === '') {
        showNotification(
          'De verkooporder in Exact heeft geen plaats, voeg deze toe in Exact.',
        );
      } else {
        this.selectedShippingCity = result.body.description2 || '';
      }
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-view-production-planning': ViewProductionPlanning;
  }
}
